import React, { useEffect, useState } from "react";
import api from "../../api/client";
import { useAuth } from "../Data/AuthContext";

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

export default function UserProfile() {
  const { user, logout } = useAuth();

  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [loadError, setLoadError] = useState("");

  const [form, setForm] = useState({ firstname: "", lastname: "", bloodType: "" });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // {kind, text}

  const [pw, setPw] = useState({ password: "", confirmPassword: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [showPwForm, setShowPwForm] = useState(false);

  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;

    (async () => {
      setStatus("loading");
      try {
        const res = await api.get(
          `/users/getuserbyemail/${encodeURIComponent(user.email)}`
        );
        if (cancelled) return;

        // The controller has returned the record under a couple of different
        // keys over time, so accept whichever shape arrives.
        const data = res.data?.user || res.data?.data || res.data;
        setProfile(data);
        setForm({
          firstname: data?.firstname || "",
          lastname: data?.lastname || "",
          bloodType: data?.bloodType || "",
        });
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err.response?.data?.message ||
            "We couldn't load your profile. Please try again."
        );
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  async function saveDetails(e) {
    e.preventDefault();
    setSaveMsg(null);

    if (!form.firstname.trim() || !form.lastname.trim()) {
      setSaveMsg({ kind: "error", text: "First and last name are both required." });
      return;
    }

    setSaving(true);
    try {
      await api.put(`/users/updateuserbyemail/${encodeURIComponent(user.email)}`, {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        bloodType: form.bloodType,
      });
      setProfile((p) => ({ ...p, ...form }));
      setSaveMsg({ kind: "success", text: "Your details have been saved." });
    } catch (err) {
      setSaveMsg({
        kind: "error",
        text: err.response?.data?.message || "Could not save your changes.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    setPwMsg(null);

    if (pw.password.length < 8) {
      setPwMsg({ kind: "error", text: "Password must be at least 8 characters." });
      return;
    }
    if (pw.password !== pw.confirmPassword) {
      setPwMsg({ kind: "error", text: "The two passwords do not match." });
      return;
    }

    setPwSaving(true);
    try {
      await api.put(`/users/updateuserbyemail/${encodeURIComponent(user.email)}`, {
        password: pw.password,
      });
      setPw({ password: "", confirmPassword: "" });
      setPwMsg({
        kind: "success",
        text: "Password updated. You'll need to sign in again.",
      });
      // The stored hash has changed, so end the session rather than leaving
      // the user on a page backed by credentials that no longer apply.
      setTimeout(() => logout(), 2500);
    } catch (err) {
      setPwMsg({
        kind: "error",
        text: err.response?.data?.message || "Could not update your password.",
      });
    } finally {
      setPwSaving(false);
    }
  }

  if (status === "loading") {
    return (
      <div className="es-page" aria-busy="true">
        <h1 className="es-h1">Your profile</h1>
        <div className="es-card">
          <div className="es-card__body">
            <div className="es-skeleton es-skeleton--text" style={{ width: "40%" }} />
            <div className="es-skeleton es-skeleton--text" style={{ width: "70%" }} />
            <div className="es-skeleton es-skeleton--text" style={{ width: "55%" }} />
          </div>
        </div>
        <span className="es-visually-hidden">Loading your profile</span>
      </div>
  );
  }

  if (status === "error") {
    return (
      <div className="es-page">
        <h1 className="es-h1">Your profile</h1>
        <div className="es-alert es-alert--error" role="alert">
          {loadError}
        </div>
        <button
          type="button"
          className="es-btn es-btn--ghost"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
  );
  }

  return (
    <div className="es-page">
      <p className="es-eyebrow">Your account</p>
      <h1 className="es-h1">
        {profile?.firstname} {profile?.lastname}
      </h1>
      <p className="es-lede">{profile?.email}</p>

      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "2rem" }}>
        <span className={`es-badge ${profile?.verified ? "es-badge--ok" : "es-badge--warn"}`}>
          {profile?.verified ? "Email verified" : "Email not verified"}
        </span>
        <span className="es-badge es-badge--neutral">
          {profile?.role === "admin" ? "Administrator" : "Member"}
        </span>
        {profile?.bloodType && (
          <span className="es-badge es-badge--neutral">Blood type {profile.bloodType}</span>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gap: "2rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(19rem, 1fr))",
          alignItems: "start",
        }}
      >
        <section className="es-card" aria-labelledby="details-heading">
          <div className="es-card__body">
            <h2 className="es-h2" id="details-heading">
              Personal details
            </h2>

            {saveMsg && (
              <div
                className={`es-alert es-alert--${saveMsg.kind === "success" ? "success" : "error"}`}
                role={saveMsg.kind === "success" ? "status" : "alert"}
              >
                {saveMsg.text}
              </div>
            )}

            <form onSubmit={saveDetails}>
              <div className="es-field">
                <label className="es-label" htmlFor="pf-first">First name</label>
                <input
                  id="pf-first"
                  className="es-input"
                  value={form.firstname}
                  onChange={(e) => setForm((f) => ({ ...f, firstname: e.target.value }))}
                />
              </div>

              <div className="es-field">
                <label className="es-label" htmlFor="pf-last">Last name</label>
                <input
                  id="pf-last"
                  className="es-input"
                  value={form.lastname}
                  onChange={(e) => setForm((f) => ({ ...f, lastname: e.target.value }))}
                />
              </div>

              <div className="es-field">
                <label className="es-label" htmlFor="pf-blood">Blood type</label>
                <select
                  id="pf-blood"
                  className="es-select"
                  value={form.bloodType}
                  onChange={(e) => setForm((f) => ({ ...f, bloodType: e.target.value }))}
                >
                  <option value="">Not specified</option>
                  {BLOOD_TYPES.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              <div className="es-field">
                <label className="es-label" htmlFor="pf-email">Email address</label>
                <input
                  id="pf-email"
                  className="es-input"
                  value={profile?.email || ""}
                  readOnly
                  aria-describedby="pf-email-hint"
                />
                <p className="es-hint" id="pf-email-hint">
                  Your address is used to sign in and can&rsquo;t be changed here.
                </p>
              </div>

              <button type="submit" className="es-btn es-btn--primary" disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </form>
          </div>
        </section>

        <section className="es-card" aria-labelledby="security-heading">
          <div className="es-card__body">
            <h2 className="es-h2" id="security-heading">Security</h2>

            {pwMsg && (
              <div
                className={`es-alert es-alert--${pwMsg.kind === "success" ? "success" : "error"}`}
                role={pwMsg.kind === "success" ? "status" : "alert"}
              >
                {pwMsg.text}
              </div>
            )}

            {!showPwForm && (
              <>
                <p className="es-body" style={{ fontSize: "0.92rem" }}>
                  Choose a new password if you think your current one may have
                  been seen by someone else.
                </p>
                <button
                  type="button"
                  className="es-btn es-btn--ghost"
                  style={{ marginTop: "1rem" }}
                  onClick={() => setShowPwForm(true)}
                >
                  Change password
                </button>
              </>
            )}

            {showPwForm && (
              <form onSubmit={changePassword}>
                <div className="es-field">
                  <label className="es-label" htmlFor="pf-pw">New password</label>
                  <input
                    id="pf-pw"
                    type="password"
                    className="es-input"
                    autoComplete="new-password"
                    value={pw.password}
                    onChange={(e) => setPw((p) => ({ ...p, password: e.target.value }))}
                  />
                  <p className="es-hint">At least 8 characters.</p>
                </div>

                <div className="es-field">
                  <label className="es-label" htmlFor="pf-pw2">Confirm new password</label>
                  <input
                    id="pf-pw2"
                    type="password"
                    className="es-input"
                    autoComplete="new-password"
                    value={pw.confirmPassword}
                    onChange={(e) => setPw((p) => ({ ...p, confirmPassword: e.target.value }))}
                  />
                </div>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button type="submit" className="es-btn es-btn--primary" disabled={pwSaving}>
                    {pwSaving ? "Updating…" : "Update password"}
                  </button>
                  <button
                    type="button"
                    className="es-btn es-btn--ghost"
                    onClick={() => {
                      setShowPwForm(false);
                      setPw({ password: "", confirmPassword: "" });
                      setPwMsg(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
